import React, { useEffect, useState } from "react";
import { Modal, Button, Form } from "react-bootstrap";
import EnrollmentService from "../../_services/EnrollmentService";
import { useEnrollmentSelection } from "./components/useEnrollmentSelection";
import SelectableEnrollmentTable, {
  headerCellStyle,
  bodyCellStyle,
} from "./components/SelectableEnrollmentTable";

interface BulkDropModulesModalProps {
  show: boolean;
  onHide: () => void;
  selectedEnquiryIds: number[];
  enquiryEnrollmentsMap: Record<number, any[]>;
  studentList: any[];
  onSuccess: () => void;
}

interface ResultRow {
  enrollmentId: number;
  status: "dropped" | "skipped" | "not_found" | "error";
  reason?: string;
  error?: string;
}

// Legacy AcademicStudentTermCourseMapping.Constants drop statuses.
const DROP_STATUS_OPTIONS = [
  { id: 3, name: "Drop for re-module" },
  { id: 5, name: "Deferred" },
  { id: 6, name: "Drop for elective" },
  { id: 7, name: "Exempted" },
  { id: 8, name: "Exempted with fee" },
];

const statusBadgeStyle = (kind: ResultRow["status"]): React.CSSProperties => {
  const pal: Record<ResultRow["status"], { bg: string; fg: string; border: string }> = {
    dropped:   { bg: "#ecfdf5", fg: "#065f46", border: "var(--status-success-border)" },
    skipped:   { bg: "#eff6ff", fg: "#1e3a8a", border: "#bfdbfe" },
    not_found: { bg: "#fefce8", fg: "#854d0e", border: "var(--status-warning-border)" },
    error:     { bg: "#fef2f2", fg: "#991b1b", border: "var(--status-danger-border)" },
  };
  const p = pal[kind];
  return {
    display: "inline-block",
    padding: "2px 8px",
    borderRadius: 10,
    background: p.bg,
    color: p.fg,
    border: `1px solid ${p.border}`,
    fontSize: "0.7rem",
    fontWeight: 600,
  };
};

const BulkDropModulesModal: React.FC<BulkDropModulesModalProps> = ({
  show,
  onHide,
  selectedEnquiryIds,
  enquiryEnrollmentsMap,
  studentList,
  onSuccess,
}) => {
  const [modules, setModules] = useState<Array<{ id: number; name: string }>>([]);
  const [loadingLookups, setLoadingLookups] = useState(false);

  const [courseId, setCourseId] = useState<number | "">("");
  const [dropStatusId, setDropStatusId] = useState<number | "">(5);
  const [remarks, setRemarks] = useState("");

  const [submitting, setSubmitting] = useState(false);
  const [results, setResults] = useState<ResultRow[] | null>(null);
  const [summary, setSummary] = useState<any>(null);
  const [error, setError] = useState("");

  const {
    enrollments,
    selectedIds,
    selectedEnrollmentIds,
    toggle,
    toggleAll,
    allSelected,
  } = useEnrollmentSelection(
    selectedEnquiryIds,
    enquiryEnrollmentsMap,
    studentList,
    show,
  );

  useEffect(() => {
    if (!show) return;
    let cancelled = false;
    setLoadingLookups(true);
    setError("");
    EnrollmentService.getLookups()
      .then((resp) => {
        if (cancelled) return;
        setModules(resp.data?.data?.modules || []);
      })
      .catch((err: any) => {
        if (!cancelled) setError(err?.response?.data?.message || "Failed to load options");
      })
      .finally(() => {
        if (!cancelled) setLoadingLookups(false);
      });
    return () => {
      cancelled = true;
    };
  }, [show]);

  const handleClose = () => {
    setCourseId("");
    setDropStatusId(5);
    setRemarks("");
    setResults(null);
    setSummary(null);
    setError("");
    onHide();
  };

  const handleSubmit = async () => {
    setError("");
    if (!courseId) {
      setError("Pick a module to drop.");
      return;
    }
    if (!dropStatusId) {
      setError("Pick a drop status.");
      return;
    }
    if (selectedEnrollmentIds.length === 0) {
      setError("Select at least one enrollment.");
      return;
    }
    setSubmitting(true);
    try {
      const resp = await EnrollmentService.bulkDropModules({
        enrollmentIds: selectedEnrollmentIds,
        courseId: Number(courseId),
        dropStatusId: Number(dropStatusId),
        remarks: remarks.trim() || null,
      });
      const data = resp.data?.data || {};
      setResults(data.results || []);
      setSummary(data.summary || null);
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || "Bulk drop modules failed");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDoneAfterResults = () => {
    handleClose();
    onSuccess();
  };

  return (
    <Modal show={show} onHide={handleClose} size="lg" backdrop="static">
      <Modal.Header closeButton>
        <Modal.Title style={{ fontSize: 16 }}>
          {results ? "Drop Modules — Results" : "Drop Modules (Bulk)"}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {error && (
          <div
            style={{
              padding: "8px 12px",
              background: "var(--status-danger-bg)",
              color: "var(--status-danger)",
              borderRadius: 6,
              marginBottom: 12,
              fontSize: 13,
            }}
          >
            {error}
          </div>
        )}

        {!results && (
          <>
            {loadingLookups ? (
              <div style={{ textAlign: "center", padding: 20, color: "var(--text-muted)" }}>
                <i className="fa fa-spinner fa-spin" /> Loading options…
              </div>
            ) : (
              <>
                <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
                  <Form.Group className="mb-3" style={{ flex: "1 1 240px" }}>
                    <Form.Label style={{ fontSize: 13, fontWeight: 600 }}>
                      Module <span style={{ color: "var(--status-danger)" }}>*</span>
                    </Form.Label>
                    <Form.Select
                      size="sm"
                      value={courseId}
                      onChange={(e) => setCourseId(e.target.value ? Number(e.target.value) : "")}
                    >
                      <option value="">Select module…</option>
                      {modules.map((co) => (
                        <option key={co.id} value={co.id}>
                          {co.name}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>

                  <Form.Group className="mb-3" style={{ flex: "1 1 200px" }}>
                    <Form.Label style={{ fontSize: 13, fontWeight: 600 }}>
                      Drop status <span style={{ color: "var(--status-danger)" }}>*</span>
                    </Form.Label>
                    <Form.Select
                      size="sm"
                      value={dropStatusId}
                      onChange={(e) => setDropStatusId(e.target.value ? Number(e.target.value) : "")}
                    >
                      {DROP_STATUS_OPTIONS.map((s) => (
                        <option key={s.id} value={s.id}>
                          {s.name}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </div>

                <Form.Group className="mb-3">
                  <Form.Label style={{ fontSize: 13, fontWeight: 600 }}>Remarks</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={2}
                    value={remarks}
                    onChange={(e) => setRemarks(e.target.value)}
                    placeholder="Optional"
                  />
                </Form.Group>

                <div
                  style={{
                    fontSize: 12,
                    color: "#854d0e",
                    background: "#fefce8",
                    border: "1px solid var(--status-warning-border)",
                    borderRadius: 6,
                    padding: "8px 10px",
                    marginBottom: 10,
                  }}
                >
                  Drops the selected module from each student that is actively enrolled in it
                  (sets status + dropped date). Revenue reversal / credit notes are not applied here.
                </div>

                <SelectableEnrollmentTable
                  enrollments={enrollments}
                  selectedIds={selectedIds}
                  allSelected={allSelected}
                  onToggle={toggle}
                  onToggleAll={toggleAll}
                  disabled={submitting}
                  caption={`Enrollments (${selectedEnrollmentIds.length} of ${enrollments.length} selected)`}
                />
              </>
            )}
          </>
        )}

        {results && (
          <>
            <div
              style={{
                display: "flex",
                gap: 12,
                marginBottom: 12,
                fontSize: 13,
                color: "var(--text-strong)",
              }}
            >
              <span>
                <strong>{summary?.total ?? results.length}</strong> processed
              </span>
              <span style={{ color: "var(--status-success-text)" }}>
                <strong>{summary?.dropped ?? 0}</strong> dropped
              </span>
              <span style={{ color: "var(--status-info-text)" }}>
                <strong>{summary?.skipped ?? 0}</strong> skipped
              </span>
              <span style={{ color: "var(--status-danger)" }}>
                <strong>{summary?.failed ?? 0}</strong> failed
              </span>
            </div>

            <div
              style={{
                maxHeight: 360,
                overflow: "auto",
                border: "1px solid var(--border-default)",
                borderRadius: 6,
              }}
            >
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr>
                    <th style={headerCellStyle}>Enrollment</th>
                    <th style={headerCellStyle}>Outcome</th>
                    <th style={headerCellStyle}>Detail</th>
                  </tr>
                </thead>
                <tbody>
                  {results.map((r) => (
                    <tr key={r.enrollmentId}>
                      <td style={bodyCellStyle}>#{r.enrollmentId}</td>
                      <td style={bodyCellStyle}>
                        <span style={statusBadgeStyle(r.status)}>{r.status}</span>
                      </td>
                      <td style={bodyCellStyle}>
                        {r.status === "dropped" && (
                          <span style={{ color: "var(--text-body)" }}>Module dropped</span>
                        )}
                        {r.status === "skipped" && (
                          <span style={{ color: "var(--text-muted)" }}>
                            {r.reason === "not_enrolled_in_course"
                              ? "Not enrolled in this module"
                              : r.reason || ""}
                          </span>
                        )}
                        {r.status === "not_found" && (
                          <span style={{ color: "#854d0e" }}>Enrollment not found</span>
                        )}
                        {r.status === "error" && (
                          <span style={{ color: "var(--status-danger)" }}>{r.error}</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </Modal.Body>

      <Modal.Footer>
        {!results ? (
          <>
            <Button variant="secondary" onClick={handleClose} disabled={submitting}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleSubmit}
              disabled={submitting || loadingLookups || !courseId || selectedEnrollmentIds.length === 0}
            >
              {submitting ? (
                <>
                  <i className="fa fa-spinner fa-spin" /> Dropping…
                </>
              ) : (
                <>Drop ({selectedEnrollmentIds.length})</>
              )}
            </Button>
          </>
        ) : (
          <Button variant="primary" onClick={handleDoneAfterResults}>
            Done
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  );
};

export default BulkDropModulesModal;
